import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { CalendarDays, MapPin, Clock, User, Search, ChevronRight, FileText } from 'lucide-react';
import api from '../../utils/api';

// Live-updating countdown to a target datetime. Ticks every second.
function useCountdown(target) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return { closed: true };
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return { closed: false, days, hours, mins, secs };
}

// Entry status for a show: closed | soon (< 7 days) | open | null (no deadline)
function entryState(show, past) {
  if (past) return { key: 'past', label: 'Completed', tone: 'neutral' };
  if (!show.entriesCloseAt) return { key: 'open', label: 'Entries open', tone: 'open' };
  const diff = new Date(show.entriesCloseAt).getTime() - Date.now();
  if (diff <= 0) return { key: 'closed', label: 'Entries closed', tone: 'closed' };
  if (diff <= 7 * 86400000) return { key: 'soon', label: 'Entries closing soon', tone: 'soon' };
  return { key: 'open', label: 'Entries open', tone: 'open' };
}

function ShowCard({ show, past }) {
  const cd = useCountdown(show.entriesCloseAt);
  const state = entryState(show, past);
  const closed = state.key === 'closed' || state.key === 'past';
  const canEnter = !closed;

  const d = new Date(show.showDate);
  const mon = format(d, 'MMM').toUpperCase();
  const day = format(d, 'd');
  const dow = format(d, 'EEE').toUpperCase();

  return (
    <div className="show-card">
      <div className="show-card-body">
        <div className="date-chip">
          <span className="date-chip-mon">{mon}</span>
          <span className="date-chip-day">{day}</span>
          <span className="date-chip-dow">{dow}</span>
        </div>

        <div className="show-card-info">
          <h3 className="show-card-title">{show.name}</h3>
          <ul className="show-meta">
            {show.judgeName && (
              <li><User size={14} /> Judge: {show.judgeName}</li>
            )}
            {show.location && (
              <li><MapPin size={14} /> {show.location}</li>
            )}
            {show.description && show.description.length < 60 && (
              <li><Clock size={14} /> {show.description}</li>
            )}
          </ul>
          <span className={`status-pill ${state.tone}`}>{state.label}</span>
        </div>
      </div>

      <div className="show-card-foot">
        <div className="show-card-foot-note">
          {state.key === 'past' || closed ? (
            <span className="muted"><FileText size={14} /> Catalogue available</span>
          ) : cd && !cd.closed && show.entriesCloseAt ? (
            <span className="muted">
              <Clock size={14} />
              <span>Entries close in <strong className="count-strong">{cd.days > 0 ? `${cd.days}d ` : ''}{cd.hours}h {cd.mins}m</strong></span>
            </span>
          ) : (
            <span className="muted"><Clock size={14} /> Entries open</span>
          )}
        </div>
        <div className="flex">
          {canEnter && (
            <Link to={`/enter?show=${show.id}`} className="btn sm">Enter a dog <ChevronRight size={14} /></Link>
          )}
          <Link to={`/catalogue/${show.id}`} className="btn ghost sm">
            {closed ? 'View catalogue' : 'Catalogue'} <ChevronRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function ShowsPage() {
  const [shows, setShows] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | open | closed
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/shows')
      .then((res) => setShows(res.data.shows))
      .finally(() => setLoading(false));
    api.get('/grades').then((res) => setGrades(res.data.grades)).catch(() => {});
  }, []);

  // Split into upcoming/current vs past based on the show date.
  const { upcoming, past } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const term = search.trim().toLowerCase();
    const matches = (s) => {
      if (term && !(`${s.name} ${s.location || ''}`.toLowerCase().includes(term))) return false;
      if (filter === 'all') return true;
      const open = !s.entriesCloseAt || new Date(s.entriesCloseAt).getTime() > Date.now();
      return filter === 'open' ? open : !open;
    };
    const up = [];
    const old = [];
    for (const s of shows) {
      if (!matches(s)) continue;
      if (new Date(s.showDate) >= today) up.push(s);
      else old.push(s);
    }
    up.sort((a, b) => new Date(a.showDate) - new Date(b.showDate));
    old.sort((a, b) => new Date(b.showDate) - new Date(a.showDate));
    return { upcoming: up, past: old };
  }, [shows, filter, search]);

  if (loading) return <div className="loading">Loading shows…</div>;

  const tabs = [
    { key: 'all', label: 'All shows' },
    { key: 'open', label: 'Entries open' },
    { key: 'closed', label: 'Entries closed' },
  ];

  return (
    <div>
      <div className="page-head">
        <p className="eyebrow">Competitions &amp; entries</p>
        <h1>Shows</h1>
        <p>Discover upcoming shows and manage your entries.</p>
      </div>

      {/* Filter tabs + search */}
      <div className="shows-toolbar">
        <div className="seg">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`seg-btn ${filter === t.key ? 'active' : ''}`}
              onClick={() => setFilter(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="shows-search">
          <Search size={16} />
          <input placeholder="Search shows…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {shows.length === 0 && <p className="muted">No published shows yet.</p>}
      {shows.length > 0 && upcoming.length === 0 && past.length === 0 && (
        <p className="muted">No shows match your filter.</p>
      )}

      {upcoming.length > 0 && (
        <>
          <div className="shows-section-head">
            <CalendarDays size={20} className="shows-section-icon" />
            <h2>Upcoming shows</h2>
            <span className="shows-section-rule" />
            <span className="shows-count">{upcoming.length} {upcoming.length === 1 ? 'show' : 'shows'}</span>
          </div>
          <div className="shows-grid">
            {upcoming.map((s) => <ShowCard key={s.id} show={s} />)}
          </div>
        </>
      )}

      {past.length > 0 && (
        <>
          <div className="shows-section-head">
            <CalendarDays size={20} className="shows-section-icon" />
            <h2>Past shows</h2>
            <span className="shows-section-rule" />
            <span className="shows-count">{past.length} {past.length === 1 ? 'show' : 'shows'}</span>
          </div>
          <div className="shows-grid">
            {past.map((s) => <ShowCard key={s.id} show={s} past />)}
          </div>
        </>
      )}

      {grades.length > 0 && (
        <>
          <div className="shows-section-head">
            <h2>Gradings explained</h2>
            <span className="shows-section-rule" />
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Age</th>
                  <th>Grading</th>
                  <th>English</th>
                  <th>German</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {grades.map((g) => {
                  let age = '—';
                  if (g.minAgeMonths != null && g.maxAgeMonths != null) age = `${g.minAgeMonths} to ${g.maxAgeMonths} months`;
                  else if (g.minAgeMonths != null) age = `Over ${g.minAgeMonths} months`;
                  return (
                    <tr key={g.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{age}</td>
                      <td style={{ fontWeight: 700 }}>{g.name}</td>
                      <td>{g.englishDescription || ''}</td>
                      <td>{g.germanName || ''}</td>
                      <td style={{ fontSize: '0.82rem' }}>{g.explanation || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="muted" style={{ marginTop: '0.5rem' }}>
            Placings are shown next to the grade in the catalogue, for example V1, V2, SG3.
          </p>
        </>
      )}
    </div>
  );
}
